"use client";

import Image from "next/image";
import { useState } from "react";

const MODULES = [
  {
    num: "01",
    title: "Aims & Advantages",
    description:
      "Grasp the purpose of group work and its power to foster deep connection, discipleship and lasting personal growth.",
    presenter: "Jaci Mun-Gavin",
    img: "/images/sgf/01---Jaci.webp",
  },
  {
    num: "02",
    title: "Benevolence & Boundaries",
    description:
      "Lead from a place of health by setting firm, non-negotiable boundaries that protect your group and prevent ministry burnout.",
    presenter: "Sharlene Soobramoney",
    img: "/images/sgf/02---Sharlene.webp",
  },
  {
    num: "03",
    title: "Culture & Communication",
    description:
      "Use active listening and empowering questions to build a dynamic culture where every group member feels seen and valued.",
    presenter: "Abner Mariri",
    img: "/images/sgf/03---Abner.webp",
  },
  {
    num: "04",
    title: "Dynamics & Denial",
    description:
      "Manage difficult dynamics, understand the stages of change, and learn to gently guide members who are struggling with denial.",
    presenter: "Damian Newalani",
    img: "/images/sgf/04---Damian.webp",
  },
  {
    num: "05",
    title: "Ethics & Etiquette",
    description:
      "Lead with integrity by applying a robust Code of Ethics and 7-step model for navigating group dilemmas with wisdom.",
    presenter: "Anthea Stanton",
    img: "/images/sgf/05---Anthea.webp",
  },
  {
    num: "06",
    title: "Function & Facilitation",
    description:
      "Master a toolkit of over 30 specific facilitation techniques to confidently guide conversations in real-world situations.",
    presenter: "Nico Mathe",
    img: "/images/sgf/06---Nicho.webp",
  },
] as const;

function ModuleCard({
  mod,
  index,
  hovered,
  onEnter,
  onLeave,
}: {
  mod: (typeof MODULES)[number];
  index: number;
  hovered: number | null;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const isHovered = hovered === index;
  let flexValue = 1;
  if (isHovered) {
    flexValue = 1.5;
  } else if (hovered !== null) {
    flexValue = 0.9;
  }
  return (
    <div
      aria-hidden="true"
      className="relative cursor-default overflow-hidden rounded-2xl transition-[flex] duration-300"
      key={mod.num}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        flex: flexValue,
      }}
    >
      <Image
        alt={`${mod.presenter}, Module ${mod.num}: ${mod.title}`}
        className="object-cover"
        fill
        sizes="(max-width: 1280px) 33vw, 420px"
        src={mod.img}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full p-5 text-white">
        {/* Number fades out on hover */}
        <p
          className="font-medium text-xs uppercase tracking-widest transition-all duration-200"
          style={{
            opacity: isHovered ? 0 : 0.7,
            maxHeight: isHovered ? 0 : "1.5rem",
            overflow: "hidden",
          }}
        >
          {mod.num}
        </p>
        <h3 className="heading-5 font-bold">
          {/* Force break at & so title is always 2 lines — keeps animation smooth */}
          {mod.title
            .replace(" & ", " &\n")
            .split("\n")
            .map((line, j) =>
              j === 0 ? (
                <span key={line}>
                  {line}
                  <br />
                </span>
              ) : (
                <span key={line}>{line}</span>
              )
            )}
        </h3>
        {/* Description slides in on hover — fixed max-w prevents reflow during transition */}
        <div
          className="overflow-hidden transition-all duration-300"
          style={{
            maxHeight: isHovered ? "8rem" : "0",
            opacity: isHovered ? 1 : 0,
            marginTop: isHovered ? "0.5rem" : 0,
          }}
        >
          <p className="body-sm max-w-[240px] text-white/85">
            {mod.description}
          </p>
        </div>
      </div>
    </div>
  );
}

function ModuleRow({
  modules,
  rowIndex,
}: {
  modules: readonly (typeof MODULES)[number][];
  rowIndex: number;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <>
      {/* Desktop: flex expand-on-hover */}
      <div className="hidden h-[760px] gap-3 sm:flex">
        {modules.map((mod, i) => (
          <ModuleCard
            hovered={hovered}
            index={i}
            key={mod.num}
            mod={mod}
            onEnter={() => setHovered(i)}
            onLeave={() => setHovered(null)}
          />
        ))}
      </div>

      {/* Mobile: stacked cards */}
      <div className="flex flex-col gap-4 sm:hidden">
        {modules.map((mod) => (
          <div
            className="relative overflow-hidden rounded-2xl"
            key={`${rowIndex}-${mod.num}`}
            style={{ height: "280px" }}
          >
            <Image
              alt={`${mod.presenter}, Module ${mod.num}: ${mod.title}`}
              className="object-cover"
              fill
              sizes="100vw"
              src={mod.img}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 text-white">
              <p className="font-medium text-xs uppercase tracking-widest opacity-70">
                {mod.num}
              </p>
              <h3 className="heading-5 font-bold">{mod.title}</h3>
              <p className="body-sm mt-1 text-white/85">{mod.description}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function SgfModulesSection() {
  const [showAll, setShowAll] = useState(false);

  const firstRow = MODULES.slice(0, 3);
  const secondRow = MODULES.slice(3, 6);

  return (
    <section className="py-20" id="section-outcomes">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading — centred, two lines */}
        <div className="mb-10 space-y-4 text-center">
          <h2 className="heading-2 font-bold">
            What You Will Learn,
            <br />
            Module by Module
          </h2>
          <p className="body-lg mx-auto max-w-2xl text-muted-foreground">
            Our six-part journey equips you with the skills to handle any small
            group situation with poise and grace.
          </p>
        </div>

        {/* First 3 modules — always visible */}
        <ModuleRow modules={firstRow} rowIndex={0} />

        {/* Last 3 — animated reveal with gradient fade */}
        <div
          className="grid transition-[grid-template-rows] duration-500 ease-in-out"
          style={{ gridTemplateRows: showAll ? "1fr" : "0fr" }}
        >
          <div className="relative min-h-0 overflow-hidden">
            {/* Gradient fades from white at the top of the expanding row — disappears once revealed */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-28 bg-gradient-to-b from-background to-transparent transition-opacity duration-500"
              style={{ opacity: showAll ? 0 : 1 }}
            />
            <div
              className="mt-3 transition-opacity delay-100 duration-300"
              style={{ opacity: showAll ? 1 : 0 }}
            >
              <ModuleRow modules={secondRow} rowIndex={1} />
            </div>
          </div>
        </div>

        {/* Toggle button */}
        <div className="mt-6 flex justify-center">
          <button
            className="rounded-full border px-6 py-2 font-medium text-muted-foreground text-sm transition-colors hover:border-foreground hover:text-foreground"
            onClick={() => setShowAll((v) => !v)}
            type="button"
          >
            {showAll ? "Show less" : "Show more"}
          </button>
        </div>
      </div>
    </section>
  );
}
